import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  Tabs,
  Tab,
  Paper,
  Chip,
  LinearProgress,
  IconButton,
  Collapse,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  School as CourseIcon,
  EmojiEvents as CompetencyIcon,
  Timeline as PathIcon,
  Minimize as MinimizeIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  moodleApi,
  MoodleSource,
  MoodleCourse,
  CompetencyFramework,
  LearningPathTemplate,
  ExportResult,
} from '../services/moodle-api';
import { environmentApi } from '../services/api';
import { useJobs } from '../contexts/JobsContext';

interface MoodleImportDialogProps {
  open: boolean;
  onClose: () => void;
  targetFolder: 'courses' | 'competency_frameworks' | 'learning_path_templates' | 'cmi5_activities';
  targetEnvironment: string;
  onImportComplete: () => void;
}

type TabValue = 'courses' | 'competency_frameworks' | 'learning_path_templates' | 'cmi5_activities';

export const MoodleImportDialog: React.FC<MoodleImportDialogProps> = ({
  open,
  onClose,
  targetFolder,
  targetEnvironment,
  onImportComplete,
}) => {
  const { refreshJobs, setShowJobsPanel } = useJobs();
  const [sources, setSources] = useState<MoodleSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState<TabValue>(targetFolder);

  // Content state
  const [courses, setCourses] = useState<MoodleCourse[]>([]);
  const [competencyFrameworks, setCompetencyFrameworks] = useState<CompetencyFramework[]>([]);
  const [learningPathTemplates, setLearningPathTemplates] = useState<LearningPathTemplate[]>([]);

  // Selection state
  const [selectedCourses, setSelectedCourses] = useState<Set<number>>(new Set());
  const [selectedFrameworks, setSelectedFrameworks] = useState<Set<number>>(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState<Set<number>>(new Set());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Local files for comparison
  const [localFiles, setLocalFiles] = useState<Record<number, { lastModified: string }>>({});

  // Filter state for courses
  const [courseFilter, setCourseFilter] = useState<'all' | 'new' | 'updated' | 'new-or-updated'>('all');

  useEffect(() => {
    if (open) {
      loadSources();
      loadLocalFiles();
    }
  }, [open, targetEnvironment, currentTab]);

  useEffect(() => {
    if (selectedSource) {
      loadContent();
    }
  }, [selectedSource, currentTab]);

  const loadLocalFiles = async () => {
    try {
      console.log('[Course Comparison] Fetching files from environment:', targetEnvironment, 'folder:', currentTab);
      const files = await environmentApi.getEnvironmentFolderFiles(targetEnvironment, currentTab);
      console.log('[Course Comparison] Fetched local files:', files);

      // Create a map of courseId -> file info by parsing filenames
      const fileMap: Record<number, { lastModified: string }> = {};
      files.forEach((file: any) => {
        // Parse course ID from filename like "CSC101-course50-20251029-150822.mbz"
        const match = file.name.match(/-course(\d+)-/);
        if (match) {
          const courseId = parseInt(match[1], 10);
          fileMap[courseId] = {
            lastModified: file.lastModified
          };
          console.log(`[Course Comparison] Mapped course ${courseId} -> ${file.lastModified}`);
        } else {
          console.log(`[Course Comparison] Could not parse course ID from: ${file.name}`);
        }
      });
      console.log('[Course Comparison] Final fileMap:', fileMap);
      setLocalFiles(fileMap);
    } catch (err) {
      console.warn('Failed to load local files for comparison:', err);
    }
  };

  const loadSources = async () => {
    setLoading(true);
    try {
      const data = await moodleApi.listSources();
      setSources(data);
      if (data.length > 0 && !selectedSource) {
        setSelectedSource(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load sources:', err);
      setError('Failed to load Moodle sources');
    } finally {
      setLoading(false);
    }
  };

  const loadContent = async () => {
    if (!selectedSource) return;

    setLoading(true);
    setError(null);

    try {
      switch (currentTab) {
        case 'courses':
          const coursesData = await moodleApi.listCourses(selectedSource);
          setCourses(coursesData);
          break;
        case 'competency_frameworks':
          const frameworksData = await moodleApi.listCompetencyFrameworks(selectedSource);
          setCompetencyFrameworks(frameworksData);
          break;
        case 'learning_path_templates':
          const templatesData = await moodleApi.listLearningPathTemplates(selectedSource);
          setLearningPathTemplates(templatesData);
          break;
      }
    } catch (err) {
      console.error('Failed to load content:', err);
      setError(err instanceof Error ? err.message : 'Failed to load content from Moodle');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedSource) return;

    const getSelectedIds = () => {
      switch (currentTab) {
        case 'courses':
          return Array.from(selectedCourses);
        case 'competency_frameworks':
          return Array.from(selectedFrameworks);
        case 'learning_path_templates':
          return Array.from(selectedTemplates);
        default:
          return [];
      }
    };

    const selectedIds = getSelectedIds();
    if (selectedIds.length === 0) {
      setError('Please select at least one item to export');
      return;
    }

    setError(null);

    // Close dialog and open jobs panel immediately
    onClose();
    setShowJobsPanel(true);

    try {
      switch (currentTab) {
        case 'courses':
          await moodleApi.exportCoursesWithProgress(
            selectedSource, 
            selectedIds, 
            targetEnvironment,
            () => {}, // Progress callback - not needed anymore
            (id) => {
              console.log(`Export job created: ${id}`);
              refreshJobs();
            }
          );
          break;
        case 'competency_frameworks':
          await moodleApi.exportCompetencyFrameworksWithProgress(
            selectedSource,
            selectedIds,
            targetEnvironment,
            () => {}, // Progress callback - not needed anymore
            (id) => {
              console.log(`Export job created: ${id}`);
              refreshJobs();
            }
          );
          break;
        case 'learning_path_templates':
          await moodleApi.exportLearningPathTemplatesWithProgress(
            selectedSource,
            selectedIds,
            targetEnvironment,
            () => {}, // Progress callback - not needed anymore
            (id) => {
              console.log(`Export job created: ${id}`);
              refreshJobs();
            }
          );
          break;
        default:
          throw new Error('Invalid export type');
      }

      // Export started successfully, notify parent
      onImportComplete();
    } catch (err) {
      console.error('Export failed:', err);
      // Dialog is already closed, so we can't show error there
      // Error will be visible in the jobs panel
    }
  };

  const handleToggleSelection = (id: number) => {
    const updateSet = (currentSet: Set<number>) => {
      const newSet = new Set(currentSet);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    };

    switch (currentTab) {
      case 'courses':
        setSelectedCourses(updateSet(selectedCourses));
        break;
      case 'competency_frameworks':
        setSelectedFrameworks(updateSet(selectedFrameworks));
        break;
      case 'learning_path_templates':
        setSelectedTemplates(updateSet(selectedTemplates));
        break;
    }
  };

  const handleSelectAll = () => {
    const filteredItems = getFilteredItems();
    const allIds = new Set(filteredItems.map((item: any) => item.id));

    switch (currentTab) {
      case 'courses':
        setSelectedCourses(allIds);
        break;
      case 'competency_frameworks':
        setSelectedFrameworks(allIds);
        break;
      case 'learning_path_templates':
        setSelectedTemplates(allIds);
        break;
    }
  };

  const handleSelectNone = () => {
    switch (currentTab) {
      case 'courses':
        setSelectedCourses(new Set());
        break;
      case 'competency_frameworks':
        setSelectedFrameworks(new Set());
        break;
      case 'learning_path_templates':
        setSelectedTemplates(new Set());
        break;
    }
  };

  const getFilteredItems = () => {
    const search = searchTerm.toLowerCase();

    switch (currentTab) {
      case 'courses':
        return courses.filter((course) => {
          // Apply search filter
          const matchesSearch =
            course.fullname.toLowerCase().includes(search) ||
            course.shortname.toLowerCase().includes(search);

          if (!matchesSearch) return false;

          // Apply status filter
          if (courseFilter === 'all') return true;

          const status = getCourseStatus(course);
          if (courseFilter === 'new-or-updated') {
            return status === 'new' || status === 'updated';
          }
          return status === courseFilter;
        });
      case 'competency_frameworks':
        return competencyFrameworks.filter(
          (fw) =>
            fw.shortname.toLowerCase().includes(search) ||
            fw.idnumber.toLowerCase().includes(search)
        );
      case 'learning_path_templates':
        return learningPathTemplates.filter(
          (tpl) =>
            tpl.name.toLowerCase().includes(search) ||
            tpl.idnumber.toLowerCase().includes(search)
        );
      default:
        return [];
    }
  };

  // Get course status (new, updated, or existing)
  const getCourseStatus = (course: MoodleCourse): 'new' | 'updated' | 'current' => {
    const localFile = localFiles[course.id];

    if (!localFile) {
      console.log(`[Course Comparison] Course ${course.id} (${course.shortname}) - NEW (not in local files)`);
      return 'new'; // Not in local S3
    }

    // Compare Moodle's timemodified (unix timestamp) with local file's lastModified (ISO string)
    const moodleModified = course.timemodified * 1000; // Convert to milliseconds
    const localModified = new Date(localFile.lastModified).getTime();

    console.log(`[Course Comparison] Course ${course.id} (${course.shortname}):`, {
      moodleModified: new Date(moodleModified).toISOString(),
      localModified: new Date(localModified).toISOString(),
      moodleNewer: moodleModified > localModified
    });

    if (moodleModified > localModified) {
      return 'updated'; // Moodle version is newer
    }

    return 'current'; // Up to date
  };

  const isSelected = (id: number): boolean => {
    switch (currentTab) {
      case 'courses':
        return selectedCourses.has(id);
      case 'competency_frameworks':
        return selectedFrameworks.has(id);
      case 'learning_path_templates':
        return selectedTemplates.has(id);
      default:
        return false;
    }
  };

  const getSelectedCount = (): number => {
    switch (currentTab) {
      case 'courses':
        return selectedCourses.size;
      case 'competency_frameworks':
        return selectedFrameworks.size;
      case 'learning_path_templates':
        return selectedTemplates.size;
      default:
        return 0;
    }
  };

  const filteredItems = getFilteredItems();
  const selectedCount = getSelectedCount();
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <DownloadIcon />
          <Box>
            <Typography variant="h6">Import from Moodle</Typography>
            <Typography variant="body2" color="text.secondary">
              Export content from live Moodle instance to {targetEnvironment}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl fullWidth margin="dense" size="small">
          <InputLabel>Moodle Source</InputLabel>
          <Select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
            label="Moodle Source"
            disabled={loading || sources.length === 0}
          >
            {sources.map((source) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name} ({source.namespace}/{source.podName})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {sources.length === 0 && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No Moodle sources configured. Please add a Moodle source first.
          </Alert>
        )}

        {selectedSource && (
          <>
            <Tabs
              value={currentTab}
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{ mt: 2, mb: 2 }}
            >
              <Tab icon={<CourseIcon />} label="Courses" value="courses" />
              <Tab icon={<CompetencyIcon />} label="Competencies" value="competency_frameworks" />
              <Tab icon={<PathIcon />} label="Learning Paths" value="learning_path_templates" />
            </Tabs>

            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 2 }}
            />

            {currentTab === 'courses' && (
              <Box sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  value={courseFilter}
                  exclusive
                  onChange={(_, newFilter) => {
                    if (newFilter !== null) {
                      setCourseFilter(newFilter);
                    }
                  }}
                  size="small"
                  fullWidth
                >
                  <ToggleButton value="all">All</ToggleButton>
                  <ToggleButton value="new-or-updated">New/Updated</ToggleButton>
                  <ToggleButton value="new">New Only</ToggleButton>
                  <ToggleButton value="updated">Updated Only</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Box display="flex" gap={1}>
                <Button size="small" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button size="small" onClick={handleSelectNone}>
                  Select None
                </Button>
              </Box>
              <Chip label={`${selectedCount} selected`} size="small" color="primary" />
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : filteredItems.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'action.hover' }}>
                <Typography color="text.secondary">
                  {searchTerm ? 'No items match your search' : 'No items found'}
                </Typography>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                <List dense>
                  {filteredItems.map((item: any) => (
                    <ListItem
                      key={item.id}
                      button
                      onClick={() => handleToggleSelection(item.id)}
                      selected={isSelected(item.id)}
                    >
                      <Checkbox
                        edge="start"
                        checked={isSelected(item.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>
                              {currentTab === 'courses'
                                ? item.fullname
                                : currentTab === 'competency_frameworks'
                                ? item.shortname
                                : item.name}
                            </span>
                            {currentTab === 'courses' && (() => {
                              const status = getCourseStatus(item);
                              if (status === 'new') {
                                return <Chip label="New" size="small" color="success" />;
                              } else if (status === 'updated') {
                                return <Chip label="Updated" size="small" color="warning" />;
                              }
                              return null;
                            })()}
                          </Box>
                        }
                        secondary={
                          currentTab === 'courses'
                            ? `${item.shortname} (ID: ${item.id})`
                            : `${item.idnumber} (ID: ${item.id})`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={!selectedSource || selectedCount === 0 || loading}
          startIcon={<DownloadIcon />}
        >
          {`Export ${selectedCount} Item${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
